/**
 * Round 110, B — lesson media (sentence audio, listening audio, illustrations)
 * downloads ONCE per lesson and is reused for every replay, remount and revisit.
 *
 * WHY THIS EXISTS (measured, Round 110): every response that carries media mints
 * a FRESH signed URL (`Expires` is in whole seconds, so two calls one second
 * apart share no URL at all), and the objects are stored with
 * `Cache-Control: private, max-age=0, no-store`. The browser's HTTP cache is
 * therefore useless twice over: a new URL is a new resource, and even the same
 * URL may not be stored. So the cache here is keyed by STABLE ASSET IDENTITY —
 * the object path in the URL, never its query string (signature/expiry) — and
 * lives in the Cache Storage API, which ignores Cache-Control entirely.
 *
 * Identity = URL pathname. Enriched rungs publish under different object paths
 * (…/b1/…, v2/…), so a raised rung can never be served baseline audio.
 *
 * Lifecycle (owner's rule): a SUCCESSFUL start of a different lesson deletes the
 * previous lessons' cached files and keeps the new one's. Completion does NOT
 * evict — the retake offer comes after completion and must reuse the files.
 *
 * Always degrades to the network: no Cache Storage (private windows), quota
 * exceeded, CORS refused, offline mid-fetch -> the caller simply gets the
 * original URL back. Nothing here ever throws to a caller.
 */
import { useEffect, useState } from 'react';

const CACHE_PREFIX = 'emi-lesson-media-v1:';
const MAX_BYTES_PER_LESSON = 80 * 1024 * 1024;
const MAX_LESSON_CACHES = 3;      // the current lesson + a couple of reviewed ones, never more
const SIZE_HEADER = 'x-emi-size';
const PREFETCH_CONCURRENCY = 2;

let currentLessonId: string | null = null;
const objectUrls = new Map<string, string>();                 // `${lessonId}|${key}` -> blob: URL
const inflight = new Map<string, Promise<string>>();
const bytesByLesson = new Map<string, number>();
const sizeKnown = new Set<string>();

/** The stable identity of an asset: its URL path. null for anything that is not an http(s) URL. */
export function mediaKey(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url, 'http://placeholder.invalid');
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.pathname;
  } catch {
    return null;
  }
}

function cacheApi(): CacheStorage | null {
  try {
    return typeof caches !== 'undefined' ? caches : null;
  } catch {
    return null; // some privacy modes throw on merely touching `caches`
  }
}

const cacheName = (lessonId: string) => CACHE_PREFIX + lessonId;
const memKey = (lessonId: string, key: string) => `${lessonId}|${key}`;
// Any syntactically valid http(s) URL works as a Cache Storage request key.
const cacheRequestUrl = (key: string) => `https://lesson-media.invalid${key}`;

function dropMemory(exceptLessonId: string | null) {
  for (const [k, blobUrl] of objectUrls) {
    if (exceptLessonId !== null && k.startsWith(exceptLessonId + '|')) continue;
    try { URL.revokeObjectURL(blobUrl); } catch { /* nothing to release */ }
    objectUrls.delete(k);
  }
  for (const id of [...bytesByLesson.keys()]) {
    if (id !== exceptLessonId) { bytesByLesson.delete(id); sizeKnown.delete(id); }
  }
}

async function deleteCaches(keep: (lessonId: string) => boolean): Promise<void> {
  const cs = cacheApi();
  if (!cs) return;
  try {
    for (const name of await cs.keys()) {
      if (!name.startsWith(CACHE_PREFIX)) continue;
      if (!keep(name.slice(CACHE_PREFIX.length))) await cs.delete(name);
    }
  } catch { /* storage unavailable: nothing to evict */ }
}

/** Round 136: the learner deleted their account - every cached lesson file on this device is removed. Never throws. */
export async function forgetAllLessonMedia(): Promise<void> {
  dropMemory(null);
  await deleteCaches(() => false);
}

/**
 * Call after a lesson START succeeds (new, resumed or reopened — the same id keeps its files).
 * Deletes every OTHER lesson's cached assets.
 */
export function beginLessonMedia(lessonId: string): void {
  currentLessonId = lessonId;
  dropMemory(lessonId);
  void deleteCaches((id) => id === lessonId);
}

/**
 * Call when a lesson is only being VIEWED (review mode): it becomes the current lesson for
 * caching, but nothing is evicted except the oldest caches beyond the small cap.
 */
export function touchLessonMedia(lessonId: string): void {
  currentLessonId = lessonId;
  void (async () => {
    const cs = cacheApi();
    if (!cs) return;
    try {
      const names = (await cs.keys()).filter((n) => n.startsWith(CACHE_PREFIX) && n !== cacheName(lessonId));
      // keys() lists oldest first; the viewed lesson + (MAX-1) newest others survive.
      for (const n of names.slice(0, Math.max(0, names.length - (MAX_LESSON_CACHES - 1)))) await cs.delete(n);
    } catch { /* ignore */ }
  })();
}

/** Synchronous: a ready-to-use blob: URL if this asset is already in memory, else null. */
export function peekLessonMedia(url: string | null | undefined): string | null {
  const key = mediaKey(url);
  if (!currentLessonId || !key) return null;
  return objectUrls.get(memKey(currentLessonId, key)) ?? null;
}

async function ensureSizeKnown(cache: Cache, lessonId: string): Promise<void> {
  if (sizeKnown.has(lessonId)) return;
  sizeKnown.add(lessonId);
  let total = 0;
  for (const req of await cache.keys()) {
    const res = await cache.match(req);
    total += Number(res?.headers.get(SIZE_HEADER) ?? 0) || 0;
  }
  bytesByLesson.set(lessonId, total);
}

/**
 * Resolve a media URL to the best URL for playing/showing it NOW: a blob: URL from memory or
 * Cache Storage, downloading and storing it first if this lesson has never needed it.
 * Resolves to the original `url` whenever caching is not possible. Never rejects.
 */
export function resolveLessonMedia(url: string | null | undefined): Promise<string> {
  if (!url) return Promise.resolve('');
  const lessonId = currentLessonId;
  const key = mediaKey(url);
  const cs = cacheApi();
  if (!lessonId || !key || !cs) return Promise.resolve(url);

  const mk = memKey(lessonId, key);
  const inMemory = objectUrls.get(mk);
  if (inMemory) return Promise.resolve(inMemory);
  const pending = inflight.get(mk);
  if (pending) return pending;

  const work = (async (): Promise<string> => {
    try {
      const cache = await cs.open(cacheName(lessonId));
      await ensureSizeKnown(cache, lessonId);
      const request = cacheRequestUrl(key);

      const hit = await cache.match(request);
      if (hit) return remember(lessonId, mk, await hit.blob());

      const net = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!net.ok) return url;
      const blob = await net.blob();
      const used = bytesByLesson.get(lessonId) ?? 0;
      if (used + blob.size <= MAX_BYTES_PER_LESSON) {
        try {
          await cache.put(request, new Response(blob, {
            headers: { 'Content-Type': net.headers.get('Content-Type') || blob.type || 'application/octet-stream', [SIZE_HEADER]: String(blob.size) },
          }));
          bytesByLesson.set(lessonId, used + blob.size);
        } catch { /* quota exceeded: still usable from memory this session */ }
      }
      // If this lesson was replaced while we were downloading, don't pin its blob in memory.
      if (currentLessonId !== lessonId) return URL.createObjectURL(blob);
      return remember(lessonId, mk, blob);
    } catch {
      return url; // CORS refused, offline, storage error -> plain network
    } finally {
      inflight.delete(mk);
    }
  })();
  inflight.set(mk, work);
  return work;
}

function remember(lessonId: string, mk: string, blob: Blob): string {
  const blobUrl = URL.createObjectURL(blob);
  if (currentLessonId === lessonId) objectUrls.set(mk, blobUrl);
  return blobUrl;
}

/** Fire-and-forget warm-up of a phase's media, two at a time, so taps find it ready. */
export function prefetchLessonMedia(urls: ReadonlyArray<string | null | undefined>): void {
  const queue = [...new Set(urls.filter((u): u is string => !!u))].filter((u) => !peekLessonMedia(u));
  const worker = async () => {
    for (let u = queue.shift(); u !== undefined; u = queue.shift()) await resolveLessonMedia(u);
  };
  for (let i = 0; i < Math.min(PREFETCH_CONCURRENCY, queue.length); i++) void worker();
}

/**
 * For elements that can wait a moment (the listening player, illustrations): the blob: URL once
 * resolved, `null` while it is being fetched. Outside a lesson it is just the URL, immediately.
 */
export function useLessonMediaUrl(url: string | null | undefined): string | null {
  const [resolved, setResolved] = useState<string | null>(() => {
    if (!url) return null;
    if (!currentLessonId || !mediaKey(url) || !cacheApi()) return url;
    return peekLessonMedia(url);
  });

  useEffect(() => {
    let alive = true;
    if (!url) { setResolved(null); return; }
    if (!currentLessonId || !mediaKey(url) || !cacheApi()) { setResolved(url); return; }
    const hit = peekLessonMedia(url);
    if (hit) { setResolved(hit); return; }
    setResolved(null);
    void resolveLessonMedia(url).then((r) => { if (alive) setResolved(r); });
    return () => { alive = false; };
  }, [url]);

  return resolved;
}

/** Test hook: forget all module state (never used by the app). */
export function __resetLessonMediaForTests(): void {
  dropMemory(null);
  inflight.clear();
  bytesByLesson.clear();
  sizeKnown.clear();
  currentLessonId = null;
}
